import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { getSupabaseClient } from '../supabase';
import { useAuth } from '../hooks/useAuth';
import {
  DEV_MODE,
  FeatureName,
  BASIC_FEATURES,
  DEV_MODE_FEATURES,
  PLAN_FEATURES,
  normalizePlanType,
} from '../config/features.config';
import {
  applyMaturityFilter,
  MODULE_CATALOG,
  nonStableModuleKeys,
  resolveNonStableKeys,
  type ModuleStatus,
} from '../config/modules.catalog';

interface FeatureFlags {
  [key: string]: boolean;
}

export interface FeatureFlagsContextValue {
  features: FeatureFlags;
  loading: boolean;
  isFeatureEnabled: (featureName: FeatureName) => boolean;
  getAllEnabledFeatures: () => FeatureName[];
  /** Tras mutar `tenant_features` o plan: refresca flags sin recargar el documento (SPA). */
  refetchFeatures: () => Promise<void>;
  isDevMode: boolean;
  /** Cuenta interna (tenants.is_internal): ve también los módulos en desarrollo/beta. */
  isInternal: boolean;
  /**
   * El plan del tenant, normalizado (`drop` | `basic` | `pro` | `enterprise`).
   *
   * 🪤 SE EXPONE PARA QUE LA PANTALLA USE EL MISMO PREDICADO QUE LA BASE. La restricción del plan
   * gratis —«sólo se vende lo importado de Smarty Drop»— vive en un trigger que pregunta
   * `tenants.plan_type = 'drop'`. Si la UI usara otra señal para decidir qué esconder (por
   * ejemplo la ausencia de `ecommerce`), habría dos definiciones de la misma regla y el día que
   * una cambie la pantalla ofrecería algo que la base rechaza.
   */
  planType: string;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextValue | null>(null);

/**
 * Los permisos recordados del ultimo ingreso.
 *
 * 🚨 SIN ESTO LA PUERTA TAPA LA APP EN CADA CARGA. `FeatureProtectedRoute` devuelve un spinner
 *    mientras `loading` es true, y eso no solo esconde la pantalla: impide que su codigo empiece
 *    siquiera a descargarse. Medido con los hitos del arranque, era el ultimo tramo grande.
 *
 * ⭐ ES EL MISMO ARRANQUE OPTIMISTA QUE YA USA `AuthContext` con `cached_tenant_id`: se trabaja con
 *    lo ultimo que se supo y se corrige por detras. Los permisos de un negocio no cambian entre un
 *    refresco y el siguiente.
 *
 * 🪤 Y ES SEGURO PORQUE ESTO NO ES LA FRONTERA. Un modulo que aparece 200 ms de mas y despues se
 *    va es un detalle visual; los DATOS los protege RLS en la base, no la creencia del navegador.
 *    Si esta cache fuera lo que autoriza, el defecto seria abrirla — no cachearla.
 *
 * 🪤 LLEVA LA VERSION DE LA APP: si un despliegue cambia el catalogo de modulos, lo guardado deja
 *    de valer. Un permiso viejo pintando una pantalla nueva es el bug mas dificil de reproducir.
 */
interface PermisosRecordados {
  version: string;
  features: FeatureFlags;
  isInternal: boolean;
  planType: string;
}

function versionDeLaApp(): string {
  return (import.meta.env?.VITE_APP_VERSION as string | undefined) ?? 'dev';
}

function claveDePermisos(tenantId: string): string {
  return `permisos_del_panel_${tenantId}`;
}

function leerPermisos(tenantId: string | null): PermisosRecordados | null {
  if (!tenantId) return null;
  try {
    const crudo = localStorage.getItem(claveDePermisos(tenantId));
    if (!crudo) return null;
    const p = JSON.parse(crudo) as PermisosRecordados;
    if (p?.version !== versionDeLaApp() || !p.features) return null;
    return p;
  } catch {
    return null;
  }
}

function guardarPermisos(tenantId: string, p: Omit<PermisosRecordados, "version">): void {
  try {
    localStorage.setItem(claveDePermisos(tenantId), JSON.stringify({ ...p, version: versionDeLaApp() }));
  } catch {
    /* sin espacio o modo incognito: se sigue pidiendo cada vez, que es como funcionaba antes */
  }
}
/**
 * Estado global de feature flags (una sola fuente de verdad para sidebar, rutas protegidas y ajustes).
 * Debe envolver la app donde exista sesión / router.
 */
export function FeatureFlagsProvider({ children }: { children: ReactNode }) {
  const { tenantId, loading: authLoading } = useAuth();
  // Lo ultimo que se supo de este negocio, leido sincronicamente: la pantalla no espera por esto.
  const recordado = leerPermisos(tenantId);
  const [features, setFeatures] = useState<FeatureFlags>(recordado?.features ?? {});
  /*
   * 🪤 `loading` ARRANCA EN FALSE SI HAY MEMORIA. Es la linea que destapa la pantalla: mientras
   *    fuera true, `FeatureProtectedRoute` mostraba el spinner y el codigo de la ruta ni se pedia.
   *    La consulta igual sale por detras y corrige lo que haga falta.
   */
  const [loading, setLoading] = useState(!recordado);
  const [isInternal, setIsInternal] = useState(recordado?.isInternal ?? false);
  const [planType, setPlanType] = useState(recordado?.planType ?? 'basic');

  const loadFeatures = useCallback(async () => {
    const supabase = getSupabaseClient();
    try {
      /*
       * ⭐ EL PLAN ES UN DATO, NO UN MÓDULO: en desarrollo se encienden todos los módulos, pero el
       *    rol del negocio (Drop / tienda) se lee de verdad. Sin esto `planType` quedaba en 'basic'
       *    en localhost y nada de lo que es del rol —el Inicio y el menú del dropshipper, la
       *    redirección de /home— se podía ver antes de producción (20-sep-2026, revisando
       *    «Revendedor Demo» en 5173). Se lee ANTES de soltar la pantalla, como en producción.
       *
       * 🚨 Y EL PLAN DROP NO SE FINGE NI EN SUS MÓDULOS. «Todo encendido» en dev le mostraba al
       *    dropshipper de 5173 «Resumen al cliente», «Enviar orden» y «Crear factura» —cosas de los
       *    planes con WhatsApp y facturación— y el founder las reportó como errores del producto.
       *    Para un negocio Drop, desarrollo carga los permisos REALES, igual que producción: es un
       *    rol acotado, y verlo con todo encendido no es verlo.
       */
      let planEnDev: string | null = null;
      if (DEV_MODE && tenantId) {
        const { data } = await supabase
          .from('tenants').select('plan_type').eq('id', tenantId).maybeSingle();
        planEnDev = (data as { plan_type?: string | null } | null)?.plan_type ?? null;
        if (planEnDev) setPlanType(normalizePlanType(planEnDev));
      }
      // Drop y almacén son roles acotados: también en desarrollo se ven con sus permisos reales.
      if (DEV_MODE && !['drop', 'almacen'].includes(normalizePlanType(planEnDev ?? undefined))) {
        if (import.meta.env?.DEV) {
          console.warn('🔧 [FeatureFlags] Modo desarrollo: TODOS los módulos habilitados');
        }
        const allFeatures: FeatureFlags = {};
        DEV_MODE_FEATURES.forEach((feature) => {
          allFeatures[feature] = true;
        });
        setIsInternal(true); // en dev se ve todo, incl. módulos en desarrollo
        setFeatures(allFeatures);
        setLoading(false);
        return;
      }

      if (!tenantId) {
        setLoading(false);
        return;
      }

      /*
       * 🚨 ACA HABIA CINCO CONSULTAS EN FILA, Y ESTE CONTEXTO TAPA LA PANTALLA MIENTRAS CARGA.
       *    `FeatureProtectedRoute` muestra un spinner hasta que esto termina, asi que sus ~825 ms de
       *    red (5 viajes x ~165 ms) bloqueaban el panel ENTERO.
       *
       *    Lo destapo la medicion, no la intuicion: en una carga con la cache de disco caliente la
       *    consulta de la lista ni salio —los datos ya estaban— y las filas igual aparecieron a los
       *    2.380 ms. Los dos segundos no eran datos: era esta espera.
       *
       * ⭐ UNA FUNCION, UN VIAJE: 4 ms medidos contra produccion. Ninguna de las cinco necesitaba el
       *    resultado de la anterior salvo `plans`, que solo queria el plan del tenant — y eso la
       *    funcion lo resuelve adentro.
       */
      const { data: permisos, error: permisosError } = await supabase
        .rpc('fn_permisos_del_panel', { p_tenant: tenantId });
      const paquete = (permisos ?? {}) as {
        tenant?: { plan_type?: string; is_internal?: boolean } | null;
        tenant_features?: Array<{ feature_name: string; is_enabled: boolean }>;
        plan_features?: unknown;
        overrides?: Array<{ feature_name: string; is_enabled: boolean }>;
        module_status?: Array<{ module_key: string; status: string }>;
      };
      const tenantData = paquete.tenant ?? null;
      const tenantError = permisosError;

      if (tenantError) {
        console.warn('⚠️ [FeatureFlags] No se pudo leer tenants.plan_type; usando basic + tenant_features', {
          tenantId,
          error: tenantError,
        });
      }

      const planType = normalizePlanType(tenantData?.plan_type as string | undefined);
      // Cuenta interna (Sandbox/Dulti/Vitalex): ve también módulos en desarrollo/beta.
      const tenantIsInternal = tenantData?.is_internal === true;
      setIsInternal(tenantIsInternal);
      // 🪤 `planType` acá es el const LOCAL de arriba (sombrea al estado del mismo nombre).
      setPlanType(planType);
      const tenantFeatures = paquete.tenant_features ?? [];
      const featuresError = permisosError;

      if (featuresError) {
        console.warn('⚠️ [FeatureFlags] No se pudo leer tenant_features; solo baseline por plan', {
          tenantId,
          planType,
          error: featuresError,
        });
      }

      // Baseline del plan. Fuente EDITABLE = plans.features (BD); fallback de fábrica =
      // PLAN_FEATURES (código). La tabla se sembró con los mismos valores, así que leer
      // BD es un no-op funcional. Si plans no responde o trae claves inválidas, caemos
      // al código: el gating nunca se queda sin baseline. Filtramos a módulos reales del
      // catálogo por defensa ante data vieja.
      let planFeatures: FeatureName[] = PLAN_FEATURES[planType] ?? PLAN_FEATURES.basic;
      // Del mismo viaje: la funcion resuelve el plan del tenant adentro y devuelve sus features.
      const planRow = { features: paquete.plan_features };
      const planError = permisosError;
      if (planError) {
        console.warn('⚠️ [FeatureFlags] No se pudo leer plans; usando PLAN_FEATURES (código)', {
          planType,
          error: planError,
        });
      } else if (Array.isArray(planRow?.features)) {
        const valid = (planRow.features as unknown[]).filter(
          (f): f is FeatureName => typeof f === 'string' && f in MODULE_CATALOG,
        );
        if (valid.length) planFeatures = valid;
      }

      /*
       * 🪤 EL PLAN MANDA SOLO. Antes acá se sumaba `BASIC_FEATURES` de forma incondicional, y
       * eso ponía un piso de POS + KDS bajo TODA cuenta: ninguna podía tener menos, y el plan
       * Drop es justamente más chico. `BASIC_FEATURES` sigue vivo donde corresponde —las ramas
       * de error de más abajo—, porque sin plan legible una cuenta usable es mejor que una
       * muerta. Para `basic`, `pro` y `enterprise` esto es un no-op: las tres ya declaran
       * `pos` y `kds` en su propia lista, en el código y en la tabla `plans`.
       */
      const featuresMap: FeatureFlags = {};
      planFeatures.forEach((feature) => {
        featuresMap[feature] = true;
      });

      if (!featuresError && tenantFeatures?.length) {
        tenantFeatures.forEach((feature) => {
          featuresMap[feature.feature_name] = feature.is_enabled;
        });
      }

      // Kill switches operacionales: overrides SIEMPRE ganan (apagar módulos en runtime sin deploy)
      const overrides = paquete.overrides ?? [];
      const overridesError = permisosError;

      if (!overridesError && overrides?.length) {
        overrides.forEach((ov) => {
          featuresMap[ov.feature_name] = ov.is_enabled;
        });
      }

      // Status EFECTIVO de módulos: override de BD (platform_module_status, editable
      // por el super admin) con fallback al catálogo (código). Así pasar un módulo a
      // "estable" tiene efecto sin deploy. Si la tabla no responde, usamos el catálogo.
      let nonStable = nonStableModuleKeys();
      const statusRows = paquete.module_status ?? [];
      const statusError = permisosError;
      if (!statusError && Array.isArray(statusRows) && statusRows.length) {
        const overrides: Record<string, ModuleStatus> = {};
        statusRows.forEach((row) => {
          overrides[row.module_key as string] = row.status as ModuleStatus;
        });
        nonStable = resolveNonStableKeys(overrides);
      }

      // Filtro de MADUREZ (última capa, gana para OCULTAR): un módulo en desarrollo/beta
      // se apaga para cuentas de cliente aunque el plan o un override lo encienda; las
      // cuentas internas (is_internal) lo siguen viendo. Ver applyMaturityFilter.
      const resueltas = applyMaturityFilter(featuresMap, tenantIsInternal, nonStable);
      setFeatures(resueltas);
      // Se recuerda para el proximo ingreso. Solo el camino EXITOSO: guardar un fallback degradado
      // dejaria a alguien con medio panel apagado hasta el siguiente despliegue.
      if (tenantId) guardarPermisos(tenantId, { features: resueltas, isInternal: tenantIsInternal, planType });
      if (import.meta.env?.DEV) {
        console.warn('🔒 [FeatureFlags] Modo producción: Features según plan');
      }
    } catch (error) {
      console.error('❌ Error al cargar features:', error);
      // Último recurso: solo tenant_features (sin plan), para no dejar ecommerce en false por un fallo puntual.
      try {
        if (tenantId) {
          const { data: rows, error: tfErr } = await supabase
            .from('tenant_features')
            .select('feature_name, is_enabled')
            .eq('tenant_id', tenantId);
          if (!tfErr && rows?.length) {
            const map: FeatureFlags = {};
            BASIC_FEATURES.forEach((f) => {
              map[f] = true;
            });
            rows.forEach((r) => {
              map[r.feature_name] = r.is_enabled;
            });
            setFeatures(map);
            return;
          }
        }
      } catch {
        /* ignore */
      }
      const fallbackFeatures: FeatureFlags = {};
      BASIC_FEATURES.forEach((feature) => {
        fallbackFeatures[feature] = true;
      });
      setFeatures(fallbackFeatures);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  /*
   * 🚨 LAS FEATURES DEL TENANT ANTERIOR SE BORRAN AL CAMBIAR DE TIENDA.
   *
   * Antes no se borraban, y el efecto era visible: al entrar a una cuenta del plan Drop desde una
   * Enterprise, el menú mostraba TODOS los módulos y dos segundos después se apagaban solos. Parece
   * un bug del sistema, y en el fondo es peor que eso: durante esos dos segundos una tienda está
   * viendo los permisos de OTRA.
   *
   * El síntoma espejo —el menú que arranca con lo básico y se va llenando— es la misma causa vista
   * desde el otro lado: el estado inicial `{}` también es una respuesta inventada.
   *
   * La regla: mientras no se sepa qué puede este tenant, no se afirma nada. `loading` queda en true
   * y quien dibuje el menú muestra un esqueleto en vez de adivinar.
   */
  useEffect(() => {
    setFeatures({});
    setLoading(true);

    if (authLoading) return;
    if (DEV_MODE) { void loadFeatures(); return; }
    if (!tenantId) return;
    void loadFeatures();
  }, [tenantId, authLoading, loadFeatures]);

  const isFeatureEnabled = useCallback(
    (featureName: FeatureName): boolean => features[featureName] ?? false,
    [features]
  );

  const getAllEnabledFeatures = useCallback((): FeatureName[] => {
    return Object.keys(features).filter((feature) => features[feature]) as FeatureName[];
  }, [features]);

  const value = useMemo<FeatureFlagsContextValue>(
    () => ({
      features,
      loading,
      isFeatureEnabled,
      getAllEnabledFeatures,
      refetchFeatures: loadFeatures,
      isDevMode: DEV_MODE,
      isInternal,
      planType,
    }),
    [features, loading, isFeatureEnabled, getAllEnabledFeatures, loadFeatures, isInternal, planType]
  );

  return <FeatureFlagsContext.Provider value={value}>{children}</FeatureFlagsContext.Provider>;
}

export function useFeatureFlags(): FeatureFlagsContextValue {
  const ctx = useContext(FeatureFlagsContext);
  if (!ctx) {
    throw new Error('useFeatureFlags debe usarse dentro de <FeatureFlagsProvider>.');
  }
  return ctx;
}
